import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, apiFetch } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Users, Plus, Mail, Trash2, Shield, Key, Activity,
  FolderGit2, Coins, Crown, UserPlus, Copy, CheckCircle2, Rocket, CreditCard,
} from "lucide-react";

const ROLE_BADGE = { owner: "text-amber-400", admin: "text-[#00F0FF]", developer: "text-sky-300", viewer: "text-slate-400" };

export default function Teams() {
  const { teamId, token } = useParams();
  if (token) return <TeamJoin token={token} />;
  return teamId ? <TeamDetail key={teamId} teamId={teamId} /> : <TeamList />;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 right-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[150px] rounded-full" />
      <header className="relative z-10 max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div><span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span> <span className="text-slate-500 text-sm">Teams</span></span></Link>
        <div className="flex items-center gap-4"><LanguageSwitcher /><Link to="/account" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Account</Link></div>
      </header>
      <main className="relative z-10 max-w-5xl mx-auto px-6 pb-24">{children}</main>
    </div>
  );
}

function TeamList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user === null) navigate("/login"); }, [user, navigate]);
  const load = useCallback(() => { apiFetch("/teams").then(setTeams).catch(() => setTeams([])); }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { const t = await apiFetch("/teams", { method: "POST", body: { name: name.trim() } }); toast.success("Team created"); navigate(`/teams/${t.id}`); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!teams) return <Shell><div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div></Shell>;

  return (
    <Shell>
      <h1 className="font-display font-extrabold text-4xl tracking-tight">Team workspaces</h1>
      <p className="text-slate-400 mt-2">Collaborate on projects, share credits, and manage deployments together.</p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 flex flex-col sm:flex-row gap-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New team name" className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0055FF]" data-testid="team-name-input" />
        <button onClick={create} disabled={busy || !name.trim()} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3 rounded-xl font-semibold disabled:opacity-40 hover:bg-[#0047d6] transition-colors duration-300" data-testid="team-create-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create team</button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="teams-list">
        {teams.length === 0 && <p className="text-slate-500 text-sm">You&apos;re not in any teams yet.</p>}
        {teams.map((t) => (
          <Link key={t.id} to={`/teams/${t.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#0055FF] transition-colors duration-300" data-testid={`team-card-${t.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-display font-bold text-lg">{t.name}</p>
              <span className={`font-mono text-[10px] uppercase ${ROLE_BADGE[t.my_role]}`}>{t.my_role}</span>
            </div>
            <p className="text-slate-500 text-xs mt-1 flex items-center gap-3"><span className="inline-flex items-center gap-1"><Users size={12} /> {t.member_count}</span><span className="inline-flex items-center gap-1"><Coins size={12} /> {t.credits?.toLocaleString()} pool</span></p>
          </Link>
        ))}
      </div>
    </Shell>
  );
}

function TeamDetail({ teamId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("developer");
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [plans, setPlans] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [seats, setSeats] = useState(null);
  const [billing, setBilling] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => { apiFetch(`/teams/${teamId}`).then(setTeam).catch(() => toast.error("Team not found")); }, [teamId]);
  const loadSeats = useCallback(() => { apiFetch(`/teams/${teamId}/billing/seats`).then(setSeats).catch(() => {}); }, [teamId]);
  useEffect(() => { if (user === null) navigate("/login"); }, [user, navigate]);
  useEffect(() => { if (user) { load(); apiFetch("/plans", { auth: false }).then(setPlans).catch(() => {}); apiFetch(`/teams/${teamId}/deployments`).then(setDeployments).catch(() => {}); } }, [user, load, teamId]);
  useEffect(() => {
    if (!user) return;
    loadSeats();
    const sid = new URLSearchParams(window.location.search).get("seat_session");
    if (sid) { apiFetch(`/teams/${teamId}/billing/seats/status/${sid}`).then(() => { toast.success("Seat billing active"); loadSeats(); window.history.replaceState(null, "", `/teams/${teamId}`); }).catch(() => {}); }
  }, [user, loadSeats, teamId]);

  const setupSeats = async () => {
    setBusy("seats");
    try { const { checkout_url } = await apiFetch(`/teams/${teamId}/billing/seats`, { method: "POST", body: { origin_url: window.location.origin } }); window.location.href = checkout_url; }
    catch (e) { toast.error(e.message); setBusy(""); }
  };

  const canManage = team && ["owner", "admin"].includes(team.my_role);
  const isOwner = team && team.my_role === "owner";

  useEffect(() => { if (canManage) apiFetch(`/teams/${teamId}/apikeys`).then(setKeys).catch(() => {}); }, [canManage, teamId]);
  useEffect(() => { if (canManage) apiFetch(`/teams/${teamId}/billing/history`).then(setBilling).catch(() => {}); }, [canManage, teamId]);

  const invite = async () => {
    if (!invEmail.trim()) return;
    setBusy("invite");
    try { await apiFetch(`/teams/${teamId}/invite`, { method: "POST", body: { email: invEmail.trim(), role: invRole } }); toast.success("Invitation sent"); setInvEmail(""); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const revokeInvite = async (id) => { try { await apiFetch(`/teams/${teamId}/invites/${id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };
  const setRole = async (uid, updates) => { try { await apiFetch(`/teams/${teamId}/members/${uid}/role`, { method: "POST", body: updates }); load(); } catch (e) { toast.error(e.message); } };
  const removeMember = async (uid) => { try { await apiFetch(`/teams/${teamId}/members/${uid}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };
  const togglePool = async (v) => { try { await apiFetch(`/teams/${teamId}`, { method: "PATCH", body: { credit_pool_enabled: v } }); load(); } catch (e) { toast.error(e.message); } };
  const createKey = async () => { try { const r = await apiFetch(`/teams/${teamId}/apikeys`, { method: "POST", body: { name: "API key" } }); setNewKey(r.api_key); setKeys((k) => [{ ...r, revoked: false }, ...k]); } catch (e) { toast.error(e.message); } };
  const revokeKey = async (id) => { try { await apiFetch(`/teams/${teamId}/apikeys/${id}`, { method: "DELETE" }); setKeys((k) => k.filter((x) => x.id !== id)); } catch (e) { toast.error(e.message); } };
  const buyPool = async (lookup) => { try { const { checkout_url } = await apiFetch(`/teams/${teamId}/billing/checkout`, { method: "POST", body: { lookup_key: lookup, origin_url: window.location.origin } }); window.location.href = checkout_url; } catch (e) { toast.error(e.message); } };
  const deleteTeam = async () => { try { await apiFetch(`/teams/${teamId}`, { method: "DELETE" }); toast.success("Team deleted"); navigate("/teams"); } catch (e) { toast.error(e.message); } };

  if (!team) return <Shell><div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div></Shell>;

  return (
    <Shell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight" data-testid="team-detail-name">{team.name}</h1>
          <p className="text-slate-500 text-sm mt-1 capitalize">You are {team.my_role} · {team.members.length} members</p>
        </div>
        <Link to="/teams" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white">All teams</Link>
      </div>

      {/* Credit pool */}
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-1.5"><Coins size={13} /> Shared pool</p>
          <p className="font-display font-extrabold text-3xl" data-testid="team-pool-credits">{team.credits?.toLocaleString()}</p>
          {isOwner && (
            <label className="flex items-center gap-2 text-xs text-slate-400 mt-2 cursor-pointer">
              <input type="checkbox" checked={!!team.credit_pool_enabled} onChange={(e) => togglePool(e.target.checked)} data-testid="team-pool-toggle" /> Enable shared pool
            </label>
          )}
        </div>
        {isOwner && plans && (
          <div className="md:col-span-2 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Team billing · fund the pool</p>
            <div className="flex flex-wrap gap-2">
              {["starter", "builder", "studio"].map((k) => (
                <button key={k} onClick={() => buyPool(plans.plans[k].lookup)} className="text-xs border border-white/15 rounded-lg px-3 py-2 hover:border-[#0055FF] transition-colors duration-300" data-testid={`team-billing-${k}`}>{plans.plans[k].name} · ${plans.plans[k].price}/mo</button>
              ))}
              <button onClick={() => buyPool("credits_25000")} className="text-xs bg-white/10 rounded-lg px-3 py-2 hover:bg-white/20" data-testid="team-billing-credits">+25k credits</button>
            </div>
          </div>
        )}
      </div>

      {/* Seat management (owner) */}
      {isOwner && seats && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="team-seats">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5"><Users size={13} /> Per-seat billing</p>
              <p className="text-sm text-slate-300"><b className="text-white" data-testid="team-seats-used">{seats.seats_used}</b> seat(s) × ${seats.seat_price}/mo = <b className="text-[#00F0FF]">${seats.monthly_total}/mo</b></p>
              <p className="text-slate-500 text-xs mt-1">{seats.active ? `Active — billed for ${seats.seats_paid} seats. Quantity auto-adjusts as members join or leave.` : "Set up per-seat billing; the subscription quantity updates automatically as your team grows."}</p>
            </div>
            <button onClick={setupSeats} disabled={busy === "seats"} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 rounded-xl text-sm hover:border-[#0055FF] disabled:opacity-40 transition-colors duration-300" data-testid="team-seats-btn">{busy === "seats" ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} {seats.active ? "Manage seats" : "Set up seat billing"}</button>
          </div>
        </div>
      )}

      {/* Members */}
      <h2 className="font-display font-bold text-xl mt-10 mb-3 flex items-center gap-2"><Users size={18} /> Members</h2>
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="team-members">
        {team.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between p-4 gap-3 flex-wrap" data-testid={`team-member-${m.user_id}`}>
            <div className="flex items-center gap-2">
              {m.role === "owner" && <Crown size={14} className="text-amber-400" />}
              <span className="text-white text-sm">{m.name || m.email}</span>
              <span className="text-slate-500 text-xs">{m.email}</span>
            </div>
            <div className="flex items-center gap-2">
              {canManage && m.role !== "owner" ? (
                <>
                  <select value={m.role} onChange={(e) => setRole(m.user_id, { role: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs outline-none" data-testid={`team-role-${m.user_id}`}>
                    {["admin", "developer", "viewer"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <label className="text-[11px] text-slate-400 flex items-center gap-1"><input type="checkbox" checked={m.deploy_allowed !== false} onChange={(e) => setRole(m.user_id, { deploy_allowed: e.target.checked })} data-testid={`team-deploy-${m.user_id}`} /> deploy</label>
                  <button onClick={() => removeMember(m.user_id)} className="text-slate-500 hover:text-red-400" data-testid={`team-remove-${m.user_id}`}><Trash2 size={14} /></button>
                </>
              ) : (
                <span className={`font-mono text-[10px] uppercase ${ROLE_BADGE[m.role]}`}>{m.role}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Invite */}
      {canManage && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0a0a0a] p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><UserPlus size={13} /> Invite a member</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={invEmail} onChange={(e) => setInvEmail(e.target.value)} type="email" placeholder="email@company.com" className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#0055FF]" data-testid="team-invite-email" />
            <select value={invRole} onChange={(e) => setInvRole(e.target.value)} className="bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none" data-testid="team-invite-role">
              {["admin", "developer", "viewer"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={invite} disabled={busy === "invite"} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-40 hover:bg-[#0047d6] transition-colors duration-300" data-testid="team-invite-btn">{busy === "invite" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Invite</button>
          </div>
          {team.invites.length > 0 && (
            <div className="mt-3 space-y-1" data-testid="team-invites">
              {team.invites.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between text-xs text-slate-400">
                  <span>{iv.email} · <span className="capitalize">{iv.role}</span> · pending</span>
                  <button onClick={() => revokeInvite(iv.id)} className="text-slate-500 hover:text-red-400" data-testid={`team-revoke-${iv.id}`}>Revoke</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* API keys */}
      {canManage && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl flex items-center gap-2"><Key size={18} /> Team API keys</h2>
            <button onClick={createKey} className="text-xs border border-white/15 rounded-lg px-3 py-1.5 hover:border-[#0055FF] inline-flex items-center gap-1.5" data-testid="team-create-key"><Plus size={13} /> New key</button>
          </div>
          {newKey && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 mb-3 flex items-center justify-between gap-3" data-testid="team-new-key">
              <code className="text-xs text-emerald-300 truncate">{newKey}</code>
              <button onClick={() => { navigator.clipboard?.writeText(newKey); toast.success("Copied"); }} className="text-slate-300 hover:text-white shrink-0"><Copy size={14} /></button>
            </div>
          )}
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="team-keys">
            {keys.length === 0 && <p className="p-4 text-slate-500 text-sm">No API keys yet.</p>}
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between p-3 text-sm">
                <span className="text-slate-300 font-mono text-xs">{k.key_prefix}••••••• · {k.name}</span>
                <button onClick={() => revokeKey(k.id)} className="text-slate-500 hover:text-red-400" data-testid={`team-revoke-key-${k.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      <h2 className="font-display font-bold text-xl mt-8 mb-3 flex items-center gap-2"><FolderGit2 size={18} /> Shared projects</h2>
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="team-projects">
        {team.projects.length === 0 && <p className="p-4 text-slate-500 text-sm">No team projects. Create one in Studio and assign it to this team.</p>}
        {team.projects.map((p) => (
          <Link key={p.id} to={`/studio/${p.id}`} className="flex items-center justify-between p-3 text-sm hover:bg-white/5">
            <span className="text-white">{p.name}</span><span className="text-slate-500 font-mono text-xs uppercase">{p.status}</span>
          </Link>
        ))}
      </div>

      {/* Shared deployments */}
      <h2 className="font-display font-bold text-xl mt-8 mb-3 flex items-center gap-2"><Rocket size={18} /> Shared deployments</h2>
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="team-deployments">
        {deployments.length === 0 && <p className="p-4 text-slate-500 text-sm">No team deployments yet.</p>}
        {deployments.map((d) => (
          <div key={d.id} className="flex items-center justify-between p-3 text-sm gap-2 flex-wrap">
            <span className="text-white capitalize flex items-center gap-2">{d.provider}
              {d.environment && <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${d.environment === "preview" ? "bg-purple-500/15 text-purple-300" : "bg-emerald-500/15 text-emerald-300"}`}>{d.environment}</span>}
            </span>
            <div className="flex items-center gap-3">
              {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[#00F0FF] text-xs">{d.url.replace("https://", "")}</a>}
              <span className={`font-mono text-[11px] uppercase ${d.state === "live" ? "text-emerald-400" : d.state === "error" ? "text-red-400" : "text-[#00F0FF]"}`}>{d.state}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Billing history */}
      {canManage && billing.length > 0 && (
        <>
          <h2 className="font-display font-bold text-xl mt-8 mb-3 flex items-center gap-2"><CreditCard size={18} /> Billing history</h2>
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="team-billing-history">
            {billing.map((b) => (
              <div key={b.id} className="flex items-center justify-between p-3 text-sm">
                <span className="text-slate-300">{b.lookup_key} <span className="text-slate-500">· {b.created_at?.slice(0, 10)}</span></span>
                <span className="text-white">${b.amount} <span className={`font-mono text-[11px] uppercase ${b.payment_status === "paid" ? "text-emerald-400" : "text-slate-500"}`}>{b.payment_status}</span></span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Activity */}
      <h2 className="font-display font-bold text-xl mt-8 mb-3 flex items-center gap-2"><Activity size={18} /> Activity</h2>
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5 max-h-64 overflow-auto" data-testid="team-activity">
        {team.activity.map((a) => (
          <p key={a.id} className="p-3 text-xs text-slate-400"><span className="text-slate-300">{a.actor_name}</span> · {a.action.replace(/_/g, " ")} <span className="text-slate-600">· {a.created_at.slice(0, 19).replace("T", " ")}</span></p>
        ))}
      </div>

      {isOwner && (
        <button onClick={deleteTeam} className="mt-8 text-sm text-slate-500 hover:text-red-400 transition-colors duration-300" data-testid="team-delete">Delete team</button>
      )}
    </Shell>
  );
}

function TeamJoin({ token }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiFetch(`/teams/invites/${token}`, { auth: false }).then(setInfo).catch(() => setInfo(null)); }, [token]);

  const accept = async () => {
    if (!user) { navigate(`/login?next=/teams/join/${token}`); return; }
    setBusy(true);
    try { const r = await apiFetch(`/teams/invites/${token}/accept`, { method: "POST" }); toast.success("You joined the team!"); navigate(`/teams/${r.team_id}`); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto mt-16 rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 text-center" data-testid="team-join">
        {info === undefined ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : info === null ? (
          <p className="text-slate-400">This invitation is invalid or already used.</p>
        ) : (
          <>
            <Users size={32} className="mx-auto text-[#00F0FF] mb-3" />
            <h1 className="font-display font-bold text-2xl">Join {info.team_name}</h1>
            <p className="text-slate-400 text-sm mt-2">{info.inviter} invited you as a <span className="capitalize text-[#00F0FF]">{info.role}</span>.</p>
            <button onClick={accept} disabled={busy} className="mt-6 w-full bg-[#0055FF] text-white py-3 rounded-xl font-semibold disabled:opacity-40 hover:bg-[#0047d6] transition-colors duration-300" data-testid="team-accept-btn">{busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : user ? "Accept invitation" : "Sign in to accept"}</button>
          </>
        )}
      </div>
    </Shell>
  );
}
